import { ChatThread } from "@/components/chat/ChatThread";
import { ChatComposer } from "@/components/chat/ChatComposer";
import { CallOverlay } from "@/components/chat/CallOverlay";
import { PreChatForm } from "@/components/widget/PreChatForm";
import { initials } from "@/lib/format";
import { mediaUrl } from "@/lib/api";
import { DOODLE_BG } from "@/lib/brand";
import { Phone, X, ArrowLeft, MessageCircle, Loader2 } from "lucide-react";

// Presentational chat panel — the chat state (session/WS/messages) lives in the
// parent (ChatWidget) so it keeps running while the bubble is closed.
export function WidgetChat({ chat, onClose, identity = null }) {
  const { config, session, messages, agentTyping, loading, starting, connected, accent, call, startSession } = chat;
  const identified = !!(identity && (identity.userId || identity.name || identity.email || identity.phone || identity.location));

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center bg-white">
        <Loader2 className="h-6 w-6 animate-spin text-[#0e8f7e]" aria-hidden="true" />
      </div>
    );
  }

  if (config?.error) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 bg-white p-6 text-center">
        <MessageCircle className="h-8 w-8 text-[#0e8f7e]" aria-hidden="true" />
        <p className="text-sm text-muted-foreground">This chat widget is unavailable.</p>
      </div>
    );
  }

  const Header = (
    <div className="flex items-center gap-2 px-3 py-2.5 text-white" style={{ background: accent }}>
      {onClose && (
        <button onClick={onClose} className="rounded-full p-1 hover:bg-white/15 md:hidden" data-testid="widget-back-button" aria-label="Back">
          <ArrowLeft className="h-5 w-5" aria-hidden="true" />
        </button>
      )}
      <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-white/20 text-base font-semibold">
        {config.widget_avatar ? <img src={mediaUrl(config.widget_avatar)} alt="" className="h-full w-full object-cover" /> : initials(config.display_name)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[15px] font-semibold leading-tight">{config.display_name}</div>
        <div className="truncate text-[12px] text-white/80">
          {session ? (connected ? "online" : "connecting…") : config.tagline}
        </div>
      </div>
      {session && config.calls_enabled !== false && (
        <button onClick={call.startCall} className="rounded-full p-2 transition-colors hover:bg-white/15" data-testid="widget-start-call-button" aria-label="Start voice call">
          <Phone className="h-5 w-5" aria-hidden="true" />
        </button>
      )}
      {onClose && (
        <button onClick={onClose} className="hidden rounded-full p-2 hover:bg-white/15 md:block" data-testid="widget-close-button" aria-label="Close">
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
      )}
    </div>
  );

  if (!session) {
    // When the host app has identified the user, skip the pre-chat form and
    // show a brief loader while their session is created.
    if (identified) {
      return (
        <div className="relative flex h-full flex-col bg-white">
          {Header}
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-[#0e8f7e]" aria-hidden="true" />
          </div>
        </div>
      );
    }
    return (
      <div className="relative flex h-full flex-col bg-white">
        {Header}
        <PreChatForm config={config} accent={accent} starting={starting} onStart={(f) => startSession(null, f)} />
      </div>
    );
  }

  return (
    <div className="relative flex h-full flex-col overflow-hidden bg-white">
      {Header}
      <ChatThread messages={messages} isMine={(m) => m.sender_type === "visitor"} typingLabel={agentTyping ? "typing" : null} wallpaper={DOODLE_BG} translationActive={!!config.translation_enabled} />
      <div style={{ backgroundColor: "#e1d9ce" }}>
        <ChatComposer onSend={chat.sendMessage} onTyping={chat.sendTyping} accent={accent} />
      </div>
      <CallOverlay call={call} label={config.display_name} accent={accent} />
    </div>
  );
}
